import { useState, useEffect, useCallback } from "react"
import { X, Plus, Star, Lock, Globe } from "lucide-react"
import { collectionAPI } from "../lib/supabase"
import { useAuth } from "../contexts/AuthContext"

export default function CollectionSelectorModal({
  isOpen,
  onClose,
  onSelectCollection,
  cardId,
  cardName = "this card"
}) {
  const { user } = useAuth()
  const [collections, setCollections] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedCollectionId, setSelectedCollectionId] = useState(null)
  const [lastUsedCollectionId, setLastUsedCollectionId] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newCollectionName, setNewCollectionName] = useState("")
  const [newCollectionDescription, setNewCollectionDescription] = useState("")
  const [isPublic, setIsPublic] = useState(true)

  const loadCollections = useCallback(async () => {
    if (!user) return

    setLoading(true)
    try {
      const result = await collectionAPI.getUserCollections(user.id)
      if (result.success && result.data) {
        setCollections(result.data)
        // Default to first collection if no last used
        if (result.data.length > 0 && !lastUsedCollectionId) {
          setSelectedCollectionId(result.data[0].id)
        }
      }
    } catch (error) {
      console.error('Error loading collections:', error)
    } finally {
      setLoading(false)
    }
  }, [user, lastUsedCollectionId])

  const loadLastUsedCollection = useCallback(() => {
    const lastUsed = localStorage.getItem(`lastUsedCollection_${user?.id}`)
    if (lastUsed) {
      setLastUsedCollectionId(parseInt(lastUsed))
      setSelectedCollectionId(parseInt(lastUsed))
    }
  }, [user?.id])

  useEffect(() => {
    if (isOpen && user) {
      loadCollections()
      loadLastUsedCollection()
    }
  }, [isOpen, user, loadCollections, loadLastUsedCollection])

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) return

    try {
      const result = await collectionAPI.createCollection(user.id, {
        name: newCollectionName.trim(),
        description: newCollectionDescription.trim(),
        is_public: isPublic
      })

      if (result.success) {
        setCollections(prev => [...prev, result.data])
        setSelectedCollectionId(result.data.id)
        setNewCollectionName("")
        setNewCollectionDescription("")
        setShowCreateForm(false)
      }
    } catch (error) {
      console.error('Error creating collection:', error)
    }
  }

  const handleSelectCollection = () => {
    if (selectedCollectionId) {
      // Save last used collection
      localStorage.setItem(`lastUsedCollection_${user?.id}`, selectedCollectionId.toString())
      onSelectCollection(selectedCollectionId)
      onClose()
    }
  }

  const getCollectionIcon = (collection) => {
    if (collection.is_default) {
      return <Star className="w-4 h-4 text-yellow-500" />
    }
    return collection.is_public ?
      <Globe className="w-4 h-4 text-green-500" /> :
      <Lock className="w-4 h-4 text-gray-500" />
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark border border-purple/20 rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-purple/20">
          <h2 className="text-lg font-semibold text-cream">
            Add {cardName} to Collection
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple"></div>
              <span className="ml-2 text-gray-400">Loading collections...</span>
            </div>
          ) : (
            <>
              {/* Collections List */}
              <div className="space-y-2 mb-4">
                {collections.map((collection) => (
                  <div
                    key={collection.id}
                    onClick={() => setSelectedCollectionId(collection.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedCollectionId === collection.id
                        ? 'border-purple bg-purple/10'
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getCollectionIcon(collection)}
                        <div>
                          <h3 className="font-medium text-cream">
                            {collection.name}
                            {collection.is_default && (
                              <span className="ml-2 text-xs text-yellow-500">(Default)</span>
                            )}
                          </h3>
                          {collection.description && (
                            <p className="text-sm text-gray-400">{collection.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-gray-400">
                        {collection.item_count || 0} cards
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Create New Collection */}
              {!showCreateForm ? (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-purple hover:text-purple transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create New Collection
                </button>
              ) : (
                <div className="border border-gray-600 rounded-lg p-4">
                  <h3 className="font-medium text-cream mb-3">Create New Collection</h3>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Collection name"
                      value={newCollectionName}
                      onChange={(e) => setNewCollectionName(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple"
                    />
                    <textarea
                      placeholder="Description (optional)"
                      value={newCollectionDescription}
                      onChange={(e) => setNewCollectionDescription(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple resize-none"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="isPublic"
                        checked={isPublic}
                        onChange={(e) => setIsPublic(e.target.checked)}
                        className="w-4 h-4 text-purple bg-gray-700 border-gray-600 rounded focus:ring-purple"
                      />
                      <label htmlFor="isPublic" className="text-sm text-gray-300">
                        Make collection public
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreateCollection}
                        disabled={!newCollectionName.trim()}
                        className="flex-1 px-4 py-2 bg-purple hover:bg-purple/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                      >
                        Create
                      </button>
                      <button
                        onClick={() => {
                          setShowCreateForm(false)
                          setNewCollectionName("")
                          setNewCollectionDescription("")
                        }}
                        className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-purple/20">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSelectCollection}
            disabled={!selectedCollectionId}
            className="flex-1 px-4 py-2 bg-purple hover:bg-purple/90 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            Add to Collection
          </button>
        </div>
      </div>
    </div>
  )
}
